import { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ant design icons
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import DisconnectOutlined from '@ant-design/icons/DisconnectOutlined';
import MinusOutlined from '@ant-design/icons/MinusOutlined';
import LeftOutlined from '@ant-design/icons/LeftOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import TrophyOutlined from '@ant-design/icons/TrophyOutlined';

// project imports
import MainCard from 'components/MainCard';
import { openSnackbar } from 'api/snackbar';
import useOnlineStatus from 'hooks/useOnlineStatus';
import SpeechTextField from 'components/SpeechTextField';

const EMPTY_TARGETS = [];

// Maintenance phase labels (drives the phase chip + dropdown).
const PHASE_LABELS = { weekly_maintenance: 'Weekly', monthly: 'Monthly', long_term: 'Long-term' };

const MaintenanceProbeMode = ({
  viewMode = 'individual',
  onDataChange,
  targets = EMPTY_TARGETS,
  // Previously-collected data for THIS session, fed back so reopening the sheet
  // restores entries instead of starting blank.
  initialData = null,
  // Offline mode props
  isOfflineMode = false,
  childId,
  childName,
  therapistId,
  therapistName,
  sessionId,
  onSave,
  onBack
}) => {
  const theme = useTheme();
  const intl = useIntl();

  // Get actual network status - auto-detect offline regardless of isOfflineMode prop
  const { isOnline } = useOnlineStatus();

  const [currentTarget, setCurrentTarget] = useState(0);
  const [probeData, setProbeData] = useState([]);
  const [sessionNotes, setSessionNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const initialRenderRef = useRef(true);

  // Phase filter — defaults to Weekly Maintenance; switch to Monthly / Long-term.
  // Display-only: probeData stays index-aligned with the full `targets` so
  // persistence is unaffected.
  const [phaseFilter, setPhaseFilter] = useState('weekly_maintenance');
  const visibleIndexes = useMemo(
    () => targets.map((_, i) => i).filter((i) => (targets[i]?.trackingStatus || '') === phaseFilter),
    [targets, phaseFilter]
  );
  // Count of targets per maintenance phase (shown in the dropdown).
  const phaseCounts = useMemo(() => {
    const c = { weekly_maintenance: 0, monthly: 0, long_term: 0 };
    targets.forEach((t) => {
      if (c[t.trackingStatus] != null) c[t.trackingStatus] += 1;
    });
    return c;
  }, [targets]);
  // Keep the individual-view cursor on a visible target when the filter changes.
  useEffect(() => {
    if (visibleIndexes.length && !visibleIndexes.includes(currentTarget)) {
      setCurrentTarget(visibleIndexes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseFilter]);

  // Always-visible phase dropdown (All / Weekly / Monthly / Long-term + counts).
  const renderPhaseFilter = () =>
    targets.length > 0 ? (
      <FormControl size="small" sx={{ minWidth: 220, alignSelf: 'flex-start' }}>
        <InputLabel>{intl.formatMessage({ id: 'form.maintenanceProbe.phaseFilter', defaultMessage: 'Phase' })}</InputLabel>
        <Select
          value={phaseFilter}
          label={intl.formatMessage({ id: 'form.maintenanceProbe.phaseFilter', defaultMessage: 'Phase' })}
          onChange={(e) => setPhaseFilter(e.target.value)}
        >
          <MenuItem value="weekly_maintenance">Weekly Maintenance ({phaseCounts.weekly_maintenance})</MenuItem>
          <MenuItem value="monthly">Monthly ({phaseCounts.monthly})</MenuItem>
          <MenuItem value="long_term">Long-term ({phaseCounts.long_term})</MenuItem>
        </Select>
      </FormControl>
    ) : null;

  // Empty-state when the chosen phase has no targets.
  const renderEmptyPhase = () => (
    <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
      <TrophyOutlined style={{ fontSize: '2.5rem', color: theme.palette.grey[300] }} />
      <Typography variant="body2" color="text.secondary">
        {intl.formatMessage({ id: 'form.maintenanceProbe.noTargetsInPhase', defaultMessage: 'No targets in this phase' })}
      </Typography>
    </Stack>
  );

  // Auto-save: whenever probeData or sessionNotes change, push form data to parent for IndexedDB persistence
  useEffect(() => {
    // Skip the initial render (no user data yet)
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    const completed = probeData.filter((p) => p.response !== null).length;
    if (completed === 0) return; // Don't auto-save empty data

    const correct = probeData.filter((p) => p.response === 'correct').length;
    const percentage = probeData.length > 0 ? Math.round((correct / probeData.length) * 100) : 0;

    const formData = {
      targets: targets.map((target, index) => ({
        targetId: target.id,
        targetName: target.name,
        response: probeData[index]?.response,
        notes: probeData[index]?.notes || ''
      })),
      sessionNotes,
      completedCount: completed,
      correctCount: correct,
      percentage
    };

    if (onDataChange) onDataChange('maintenance-probe', formData);
  }, [probeData, sessionNotes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize probeData when targets change; start the cursor on the first
  // target of the current phase (default Weekly).
  useEffect(() => {
    if (targets.length > 0) {
      // Rehydrate from this session's already-collected data (initialData) when
      // present, so reopening the sheet restores entries; blank otherwise.
      const savedById = new Map((initialData?.targets || []).map((s) => [String(s.targetId), s]));
      setProbeData(
        targets.map((target) => {
          const saved = savedById.get(String(target.id));
          return {
            targetId: target.id,
            response: saved?.response ?? null,
            notes: saved?.notes || ''
          };
        })
      );
      if (initialData?.sessionNotes) setSessionNotes(initialData.sessionNotes);
      const firstVisible = targets.findIndex((t) => (t.trackingStatus || '') === phaseFilter);
      setCurrentTarget(firstVisible >= 0 ? firstVisible : 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets]);

  const handleResponse = (response) => {
    if (probeData.length === 0) return;

    const newProbeData = [...probeData];
    newProbeData[currentTarget].response = response;
    setProbeData(newProbeData);

    // Auto-save handled by useEffect on state changes → onDataChange → parent's persistToIndexedDB (upsert)

    // Auto-advance to the next VISIBLE target (respects the phase filter)
    const pos = visibleIndexes.indexOf(currentTarget);
    if (pos !== -1 && pos < visibleIndexes.length - 1) {
      const next = visibleIndexes[pos + 1];
      setTimeout(() => setCurrentTarget(next), 300);
    }
  };

  const handleGridResponse = (targetIndex, response) => {
    const newProbeData = [...probeData];
    newProbeData[targetIndex].response = response;
    setProbeData(newProbeData);

    // Auto-save handled by useEffect on state changes → onDataChange → parent's persistToIndexedDB (upsert)
  };

  const handleNotesChange = (targetIndex, notes) => {
    const newProbeData = [...probeData];
    newProbeData[targetIndex].notes = notes;
    setProbeData(newProbeData);

    if (onDataChange) {
      onDataChange();
    }
  };

  const getProgress = () => {
    const completed = probeData.filter((p) => p.response !== null).length;
    const correct = probeData.filter((p) => p.response === 'correct').length;
    const percentage = probeData.length > 0 ? Math.round((correct / probeData.length) * 100) : 0;
    return { completed, correct, percentage };
  };

  const getStatusColor = (score) => {
    if (!score) return 'default';
    if (score === '✓') return 'success';
    if (score === '✗') return 'error';
    const percentage = parseInt(score);
    if (percentage >= 90) return 'success';
    if (percentage >= 70) return 'warning';
    return 'error';
  };

  // Handle save - uses offline-first pattern (always saves to IndexedDB, syncs in background)
  const handleSave = async (showNotification = true) => {
    setSaving(true);
    try {
      const { completed, correct, percentage } = getProgress();

      // Don't save if no data
      if (completed === 0) {
        setSaving(false);
        return;
      }

      const formData = {
        targets: targets.map((target, index) => ({
          targetId: target.id,
          targetName: target.name,
          response: probeData[index]?.response,
          notes: probeData[index]?.notes || ''
        })),
        sessionNotes,
        completedCount: completed,
        correctCount: correct,
        percentage
      };

      // Notify parent to persist via its upsert pattern (persistToIndexedDB)
      if (onDataChange) {
        onDataChange('maintenance-probe', formData);
      }

      if (showNotification) {
        openSnackbar({
          open: true,
          message: isOnline
            ? intl.formatMessage({ id: 'form.msg.dataSaved' }, { formName: intl.formatMessage({ id: 'form.maintenanceProbe.title' }) })
            : intl.formatMessage(
                { id: 'form.msg.dataSavedOffline' },
                { formName: intl.formatMessage({ id: 'form.maintenanceProbe.title' }) }
              ),
          variant: 'alert',
          alert: { color: 'success' }
        });
      }

      if (onSave) onSave();
      if (isOfflineMode && onBack) onBack();
    } catch (error) {
      console.error('Error saving entry:', error);
      if (showNotification) {
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'form.msg.saveFailed' }),
          variant: 'alert',
          alert: { color: 'error' }
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // Alias for backward compatibility with existing UI buttons
  const handleOfflineSave = handleSave;

  // Individual View
  if (viewMode === 'individual') {
    // Early return if no targets
    if (targets.length === 0 || probeData.length === 0) {
      return (
        <MainCard>
          <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
            <TrophyOutlined style={{ fontSize: '4rem', color: theme.palette.grey[300] }} />
            <Typography variant="h5" color="text.secondary">
              {intl.formatMessage({ id: 'form.maintenanceProbe.noMaintenanceTargets' })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage({ id: 'form.maintenanceProbe.noMasteredSkills' })}
            </Typography>
          </Stack>
        </MainCard>
      );
    }

    const target = visibleIndexes.includes(currentTarget) ? targets[currentTarget] : null;

    // Chosen phase has no targets — show the dropdown + an empty state.
    if (!target) {
      return (
        <Box sx={isOfflineMode ? { p: { xs: 2, sm: 3 } } : {}}>
          <Stack spacing={2.5}>
            {renderPhaseFilter()}
            {renderEmptyPhase()}
          </Stack>
        </Box>
      );
    }

    const { completed, correct, percentage } = getProgress();

    return (
      <Box sx={isOfflineMode ? { p: { xs: 2, sm: 3 } } : {}}>
        {/* Network Lost Banner - shown when not in explicit offline mode but network is down */}
        {!isOfflineMode && !isOnline && (
          <Alert severity="info" icon={<DisconnectOutlined />} sx={{ mb: 2 }}>
            <Typography variant="body2">{intl.formatMessage({ id: 'form.msg.offlineNote' })}</Typography>
          </Alert>
        )}

        {/* Offline Mode Header */}
        {isOfflineMode && (
          <MainCard sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button variant="text" startIcon={<LeftOutlined />} onClick={onBack}>
                  {intl.formatMessage({ id: 'form.button.back' })}
                </Button>
                <Box>
                  <Typography variant="h5">{intl.formatMessage({ id: 'form.maintenanceProbe.headerTitle' }, { childName })}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {intl.formatMessage({ id: 'form.label.offlineMode' })}
                  </Typography>
                </Box>
              </Stack>
              <Button variant="contained" startIcon={<SaveOutlined />} onClick={handleOfflineSave} disabled={saving || completed === 0}>
                {saving ? intl.formatMessage({ id: 'form.button.saving' }) : intl.formatMessage({ id: 'form.button.saveOffline' })}
              </Button>
            </Stack>
          </MainCard>
        )}

        <Stack spacing={3}>
          {renderPhaseFilter()}
          {/* Session Progress */}
          <Box sx={{ width: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {intl.formatMessage({ id: 'form.maintenanceProbe.progress' })}
              </Typography>
              <Typography variant="caption" color="primary" fontWeight={600}>
                {intl.formatMessage(
                  { id: 'form.maintenanceProbe.progressDetail' },
                  { completed, total: targets.length, correct, percentage }
                )}
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={(completed / targets.length) * 100} sx={{ height: 6, borderRadius: 3 }} />
          </Box>

          {/* Target Card — compact, aligned layout */}
          <MainCard>
            <Stack spacing={2}>
              {/* Top row: target counter (left) + phase chip (right) */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {intl.formatMessage(
                    { id: 'form.label.targetOfTotal' },
                    { current: visibleIndexes.indexOf(currentTarget) + 1, total: visibleIndexes.length }
                  )}
                </Typography>
                {PHASE_LABELS[target.trackingStatus] && (
                  <Chip size="small" color="secondary" variant="outlined" label={`${PHASE_LABELS[target.trackingStatus]} Maintenance`} />
                )}
              </Stack>

              {/* Target name + description */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" fontWeight={700}>
                  {target.name}
                </Typography>
                {target.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {target.description}
                  </Typography>
                )}
              </Box>

              {/* Compact info strip: mastered + last probe (clean, aligned row) */}
              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                divider={<Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'divider' }} />}
              >
                {target.masteredDate && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <TrophyOutlined style={{ fontSize: '0.85rem', color: theme.palette.success.main }} />
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'form.maintenanceProbe.previouslyMastered' })}:&nbsp;
                      <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        {target.masteredDate}
                      </Box>
                    </Typography>
                  </Stack>
                )}
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {intl.formatMessage({ id: 'form.maintenanceProbe.lastProbeDate' })}:&nbsp;
                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      {target.lastProbeDate ||
                        intl.formatMessage({ id: 'form.maintenanceProbe.noProbesYet', defaultMessage: 'No probes yet' })}
                    </Box>
                  </Typography>
                  {target.lastProbeScore && (
                    <Chip
                      size="small"
                      color={getStatusColor(target.lastProbeScore)}
                      label={target.lastProbeScore}
                      sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontWeight: 700 } }}
                    />
                  )}
                </Stack>
              </Stack>

              {/* Response Buttons — compact, always visible, centered */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Grid container spacing={1.5} sx={{ maxWidth: 560, width: '100%' }}>
                  {[
                    {
                      value: 'correct',
                      color: 'success',
                      icon: <CheckOutlined style={{ fontSize: '1.3rem' }} />,
                      label: intl.formatMessage({ id: 'common.correct' })
                    },
                    {
                      value: 'incorrect',
                      color: 'error',
                      icon: <CloseOutlined style={{ fontSize: '1.3rem' }} />,
                      label: intl.formatMessage({ id: 'common.incorrect' })
                    },
                    {
                      value: 'no-response',
                      color: 'secondary',
                      icon: <MinusOutlined style={{ fontSize: '1.3rem' }} />,
                      label: intl.formatMessage({ id: 'common.noResponse' })
                    }
                  ].map((btn) => (
                    <Grid size={{ xs: 4 }} key={btn.value}>
                      <Button
                        variant={probeData[currentTarget]?.response === btn.value ? 'contained' : 'outlined'}
                        color={btn.color}
                        fullWidth
                        onClick={() => handleResponse(btn.value)}
                        sx={{ flexDirection: 'column', gap: 0.25, py: 1.25, fontWeight: 700 }}
                      >
                        {btn.icon}
                        <span style={{ fontSize: '0.8rem' }}>{btn.label}</span>
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Response History dots */}
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
                {visibleIndexes.map((index, pos) => {
                  const probe = probeData[index] || { response: null };
                  const isCompleted = probe.response !== null;
                  const isCurrent = index === currentTarget;
                  return (
                    <Box
                      key={index}
                      onClick={() => setCurrentTarget(index)}
                      sx={{
                        width: 36,
                        height: 36,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 2,
                        borderColor: isCurrent ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        bgcolor: isCompleted
                          ? probe.response === 'correct'
                            ? 'success.lighter'
                            : probe.response === 'incorrect'
                              ? 'error.lighter'
                              : 'grey.200'
                          : 'grey.50',
                        color: isCompleted
                          ? probe.response === 'correct'
                            ? 'success.main'
                            : probe.response === 'incorrect'
                              ? 'error.main'
                              : 'text.disabled'
                          : 'text.disabled',
                        fontWeight: 600,
                        fontSize: '0.8rem'
                      }}
                    >
                      {isCompleted ? (
                        probe.response === 'correct' ? (
                          <CheckOutlined />
                        ) : probe.response === 'incorrect' ? (
                          <CloseOutlined />
                        ) : (
                          <MinusOutlined />
                        )
                      ) : (
                        pos + 1
                      )}
                    </Box>
                  );
                })}
              </Stack>

              {/* Notes */}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder={intl.formatMessage({ id: 'form.maintenanceProbe.addProbeNotes' })}
                value={probeData[currentTarget]?.notes || ''}
                onChange={(e) => handleNotesChange(currentTarget, e.target.value)}
              />
            </Stack>
          </MainCard>

          {/* Session Notes - Offline Mode Only */}
          {isOfflineMode && (
            <MainCard>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {intl.formatMessage({ id: 'form.label.sessionNotes' })}
              </Typography>
              <SpeechTextField
                fullWidth
                multiline
                rows={3}
                placeholder={intl.formatMessage({ id: 'form.label.notesPlaceholder' })}
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
              />
            </MainCard>
          )}
        </Stack>
      </Box>
    );
  }

  // Grid View
  // Early return if no targets
  if (targets.length === 0 || probeData.length === 0) {
    return (
      <MainCard>
        <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
          <TrophyOutlined style={{ fontSize: '4rem', color: theme.palette.grey[300] }} />
          <Typography variant="h5" color="text.secondary">
            {intl.formatMessage({ id: 'form.maintenanceProbe.noMaintenanceTargets' })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {intl.formatMessage({ id: 'form.maintenanceProbe.noMasteredSkills' })}
          </Typography>
        </Stack>
      </MainCard>
    );
  }

  return (
    <MainCard>
      {/* Network Lost Banner - shown when not in explicit offline mode but network is down */}
      {!isOfflineMode && !isOnline && (
        <Alert severity="info" icon={<DisconnectOutlined />} sx={{ mb: 2 }}>
          <Typography variant="body2">{intl.formatMessage({ id: 'form.msg.offlineNote' })}</Typography>
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack>
            <Typography variant="h5">{intl.formatMessage({ id: 'form.maintenanceProbe.checklist' })}</Typography>
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage({ id: 'form.maintenanceProbe.checkDescription' })}
            </Typography>
          </Stack>
          <Paper sx={{ p: 2, bgcolor: 'success.lighter' }}>
            <Typography variant="h4" fontWeight={700} color="success.main">
              {getProgress().percentage}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {intl.formatMessage({ id: 'form.maintenanceProbe.maintained' })}
            </Typography>
          </Paper>
        </Stack>

        {renderPhaseFilter()}

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50}>#</TableCell>
                <TableCell>{intl.formatMessage({ id: 'form.maintenanceProbe.target' })}</TableCell>
                <TableCell width={120}>{intl.formatMessage({ id: 'form.maintenanceProbe.mastered' })}</TableCell>
                <TableCell width={120}>{intl.formatMessage({ id: 'form.maintenanceProbe.lastProbe' })}</TableCell>
                <TableCell width={100}>{intl.formatMessage({ id: 'form.maintenanceProbe.lastScore' })}</TableCell>
                <TableCell width={200}>{intl.formatMessage({ id: 'form.maintenanceProbe.response' })}</TableCell>
                <TableCell>{intl.formatMessage({ id: 'form.label.notes' })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleIndexes.map((index, pos) => {
                const target = targets[index];
                return (
                  <TableRow key={target.id} hover>
                    <TableCell>{pos + 1}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TrophyOutlined style={{ color: theme.palette.success.main }} />
                        <Stack>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {target.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {target.description}
                          </Typography>
                          {PHASE_LABELS[target.trackingStatus] && (
                            <Chip
                              size="small"
                              variant="outlined"
                              color="secondary"
                              label={PHASE_LABELS[target.trackingStatus]}
                              sx={{ height: 18, fontSize: '0.65rem', mt: 0.5, alignSelf: 'flex-start' }}
                            />
                          )}
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{target.masteredDate}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{target.lastProbeDate}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={target.lastProbeScore} size="small" color={getStatusColor(target.lastProbeScore)} variant="filled" />
                    </TableCell>
                    <TableCell>
                      <ToggleButtonGroup
                        value={probeData[index]?.response}
                        exclusive
                        onChange={(e, value) => value && handleGridResponse(index, value)}
                        size="small"
                      >
                        <ToggleButton value="correct" color="success">
                          <CheckOutlined />
                        </ToggleButton>
                        <ToggleButton value="incorrect" color="error">
                          <CloseOutlined />
                        </ToggleButton>
                        <ToggleButton value="no-response" color="secondary">
                          <MinusOutlined />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder={intl.formatMessage({ id: 'form.maintenanceProbe.notesPlaceholder' })}
                        value={probeData[index]?.notes || ''}
                        onChange={(e) => handleNotesChange(index, e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </MainCard>
  );
};

MaintenanceProbeMode.propTypes = {
  viewMode: PropTypes.string,
  onDataChange: PropTypes.func,
  targets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      masteredDate: PropTypes.string,
      lastProbeDate: PropTypes.string,
      lastProbeScore: PropTypes.string,
      domain: PropTypes.string,
      image: PropTypes.string
    })
  ),
  // Offline mode props
  isOfflineMode: PropTypes.bool,
  childId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  childName: PropTypes.string,
  therapistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  therapistName: PropTypes.string,
  onSave: PropTypes.func,
  onBack: PropTypes.func
};

export default MaintenanceProbeMode;
